<script setup lang="ts">
import { ref } from 'vue'
import { useVehicleStore } from '../../stores/vehicleStore'
import { useSearchHistoryStore } from '../../stores/searchHistoryStore'
import { get } from '../../api/client'

interface MotorCodeVehicle {
  carId: number
  carName: string
}

const vehicleStore = useVehicleStore()
const motorCodeInput = ref('')
const isSearching = ref(false)
const error = ref<string | null>(null)
const vehicles = ref<MotorCodeVehicle[]>([])
const showResults = ref(false)

async function search() {
  const val = motorCodeInput.value.trim()
  if (val.length < 2) return

  isSearching.value = true
  error.value = null
  vehicles.value = []
  showResults.value = false

  try {
    vehicles.value = await get<MotorCodeVehicle[]>(`/vehicle/by-motorcode/${encodeURIComponent(val)}`)
    showResults.value = true
    if (vehicles.value.length === 0) {
      error.value = 'Ingen kjøretøy funnet for denne motorkoden'
    } else if (vehicles.value.length === 1) {
      // Auto-select if only one match
      await selectVehicle(vehicles.value[0].carId)
    }
  } catch (e: any) {
    error.value = e.message || 'Søk feilet'
  } finally {
    isSearching.value = false
  }
}

const historyStore = useSearchHistoryStore()

async function selectVehicle(carId: number) {
  const car = vehicles.value.find(v => v.carId === carId)
  await vehicleStore.selectMotor(carId)

  historyStore.addEntry({
    type: 'motorcode',
    query: motorCodeInput.value.trim().toUpperCase(),
    carName: car?.carName || vehicleStore.carDetails?.manuName || '',
    carId: carId,
  })
}
</script>

<template>
  <div class="motorcode-search">
    <h3>Søk på motorkode</h3>
    <form class="motorcode-search__form" @submit.prevent="search">
      <div class="motorcode-search__row">
        <input
          v-model="motorCodeInput"
          type="text"
          placeholder="F.eks. D4204T5"
          class="motorcode-search__input"
          :disabled="isSearching"
        />
        <button
          type="submit"
          class="motorcode-search__btn"
          :disabled="isSearching || motorCodeInput.trim().length < 2"
        >
          <span v-if="isSearching" class="spinner"></span>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </button>
      </div>
    </form>

    <div v-if="error" class="motorcode-search__error">{{ error }}</div>

    <!-- Multiple vehicle results -->
    <div v-if="showResults && vehicles.length > 1" class="motorcode-search__results">
      <p class="motorcode-search__count">{{ vehicles.length }} kjøretøy funnet — velg din:</p>
      <ul class="motorcode-search__list">
        <li
          v-for="v in vehicles"
          :key="v.carId"
          class="motorcode-search__item"
          @click="selectVehicle(v.carId)"
        >
          {{ v.carName }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.motorcode-search {
  max-width: 400px;
  margin: 0 auto;

  h3 { margin-bottom: 0.75rem; }

  &__row {
    display: flex;
    border: 2px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
  }

  &__input {
    flex: 1;
    border: none;
    padding: 12px;
    font-size: 1rem;
    text-transform: uppercase;
    outline: none;
    min-width: 0;

    &:focus { box-shadow: inset 0 0 0 1px #002868; }
  }

  &__btn {
    background: #002868;
    color: white;
    border: none;
    padding: 0 16px;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  &__error {
    margin-top: 0.75rem;
    padding: 0.5rem;
    background: #fef2f2;
    color: #991b1b;
    border-radius: 6px;
    font-size: 0.85rem;
  }

  &__results {
    margin-top: 1rem;
    text-align: left;
  }

  &__count {
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 0.5rem;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__item {
    padding: 0.6rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    border: 1px solid #e5e7eb;
    margin-bottom: 0.25rem;

    &:hover { background: #f0f4f8; border-color: #002868; }
  }
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
